import axios from 'axios';

// Token management utilities
const TOKEN_KEY = 'ccbc_access_token';
const REFRESH_TOKEN_KEY = 'ccbc_refresh_token';

// Secure token storage
const tokenManager = {
  getToken: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error('Error accessing token from localStorage:', error);
      return null;
    }
  },
  
  setToken: (token) => {
    try {
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch (error) {
      console.error('Error storing token in localStorage:', error);
    }
  },
  
  getRefreshToken: () => {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Error accessing refresh token from localStorage:', error);
      return null;
    }
  },
  
  setRefreshToken: (refreshToken) => {
    try {
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      console.error('Error storing refresh token in localStorage:', error);
    }
  },
  
  clearTokens: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Error clearing tokens from localStorage:', error);
    }
  }
};

// Dynamically determine API base URL
const getApiBaseUrl = () => {
  // Use environment variable if set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Production API URL
  if (process.env.NODE_ENV === 'production') {
    return 'https://ccbc-api.chengkayu.com';
  }
  
  // If accessing via network IP, use the same IP for direct API access
  const currentHost = window.location.hostname;
  if (currentHost === '192.168.1.4') {
    return 'http://192.168.1.4:8001';
  }else if(currentHost === '18.142.219.23') {
    return 'http://18.142.219.23:8001';
  }
  else if (currentHost === 'localhost') {
    // If running via docker-compose, frontend is usually on 3001 and API is mapped to 8001
    if (window.location.port === '3001') {
      return 'http://localhost:8001';
    }
    // Default FastAPI start.sh uses port 8000 when running locally without Docker
    return 'http://localhost:8000';
  }else if (currentHost === 'ccbc.chengkayu.com') {
    return 'https://ccbc-api.chengkayu.com';
  }else if (currentHost === 'ccbc-dev.chengkayu.com') {
    return 'https://ccbc-dev-api.chengkayu.com';
  } else if (currentHost === 'admin.dev.ccbc.org.hk') {
    return 'https://api.dev.ccbc.org.hk';
  } else if (currentHost === 'admin.ccbc.org.hk') {
    return 'https://api.ccbc.org.hk';
  }

  // Default to localhost for local development
  return 'http://localhost:8001';
  // return 'https://ccbc-dev-api.chengkayu.com';
};

const API_BASE_URL = getApiBaseUrl();

console.log('API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Token refresh state — prevent concurrent refresh calls
let _isRefreshing = false;
let _refreshSubscribers = [];

const _onRefreshed = (token) => {
  _refreshSubscribers.forEach(cb => cb(token));
  _refreshSubscribers = [];
};

const _subscribeRefresh = (cb) => _refreshSubscribers.push(cb);

const _getTokenExpiresInSeconds = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp - Date.now() / 1000;
  } catch {
    return 0;
  }
};

const _doRefresh = async () => {
  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');
  const res = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, { refresh_token: refreshToken });
  const { access_token } = res.data;
  tokenManager.setToken(access_token);
  return access_token;
};

// Request interceptor - Add Bearer token to all requests
api.interceptors.request.use(
  async (config) => {
    // Skip auth header for the refresh endpoint itself
    if (config.url?.includes('/auth/refresh') || config.url?.includes('/auth/login')) {
      config.metadata = { startTime: new Date() };
      return config;
    }

    let token = tokenManager.getToken();

    // Proactively refresh if token expires in < 5 minutes
    if (token && _getTokenExpiresInSeconds(token) < 300) {
      try {
        if (!_isRefreshing) {
          _isRefreshing = true;
          token = await _doRefresh();
          _isRefreshing = false;
          _onRefreshed(token);
        } else {
          // Another request is already refreshing — wait for it
          token = await new Promise(resolve => _subscribeRefresh(resolve));
        }
      } catch (e) {
        _isRefreshing = false;
        tokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(e);
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.metadata = { startTime: new Date() };

    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh and errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      const duration = new Date() - response.config.metadata.startTime;
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`, response.data);
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized — reactive fallback refresh
    if (error.response?.status === 401 && !originalRequest._retry
        && !originalRequest.url?.includes('/auth/')) {
      originalRequest._retry = true;
      
      try {
        let newToken;
        if (_isRefreshing) {
          newToken = await new Promise(resolve => _subscribeRefresh(resolve));
        } else {
          _isRefreshing = true;
          newToken = await _doRefresh();
          _isRefreshing = false;
          _onRefreshed(newToken);
        }
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        _isRefreshing = false;
        console.error('❌ Token refresh failed:', refreshError);
        tokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('❌ Access forbidden - insufficient permissions');
      // You might want to show a permission denied message
    }
    
    // Handle network errors
    if (!error.response) {
      console.error('❌ Network error - API server unreachable');
    }
    
    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`, {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        data: error.response?.data
      });
    }
    
    return Promise.reject(error);
  }
);

// Enhanced API functions with built-in error handling
export const apiService = {
  // GET request with Bearer token
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'GET request failed');
    }
  },

  // POST request with Bearer token
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'POST request failed');
    }
  },

  // PUT request with Bearer token
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'PUT request failed');
    }
  },

  // DELETE request with Bearer token
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'DELETE request failed');
    }
  },

  // PATCH request with Bearer token
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await api.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'PATCH request failed');
    }
  },

  // File upload with Bearer token
  uploadFile: async (url, file, onUploadProgress = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onUploadProgress || ((progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${progress}%`);
        })
      };
      
      const response = await api.post(url, formData, config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'File upload failed');
    }
  }
};

// Security utilities
export const securityUtils = {
  // Check if token is expired (basic JWT decode)
  isTokenExpired: (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  },

  // Get token expiration time
  getTokenExpiration: (token) => {
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  },

  // Validate current token
  validateCurrentToken: () => {
    const token = tokenManager.getToken();
    if (!token || securityUtils.isTokenExpired(token)) {
      tokenManager.clearTokens();
      return false;
    }
    return true;
  }
};

// Export token manager for external use
export { tokenManager };

export default api;
